import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Home, TrendingUp, Shield, Key, Phone, Mail, MapPin, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

const Immobilienmakler = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const services = [
    {
      icon: Home,
      title: "Immobilienverkauf",
      description: "Professionelle Vermarktung Ihrer Immobilie zum bestmöglichen Preis mit modernsten Marketing-Strategien."
    },
    {
      icon: Key,
      title: "Vermietung",
      description: "Zuverlässige Vermittlung von Mietobjekten mit sorgfältiger Mieterauswahl und umfassender Betreuung."
    },
    {
      icon: TrendingUp,
      title: "Immobilienbewertung",
      description: "Kostenlose und unverbindliche Wertermittlung Ihrer Immobilie durch zertifizierte Gutachter."
    },
    {
      icon: Shield,
      title: "Rundum-Service",
      description: "Von der Besichtigung bis zum Notartermin - wir begleiten Sie durch den gesamten Prozess."
    }
  ];

  const properties = [
    {
      image: "https://images.unsplash.com/photo-1570129477492-45c003edd2be?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHwxfHxyZWFsJTIwZXN0YXRlJTIwaG9tZXxlbnwwfHx8fDE3NzM1Nzk2OTd8MA&ixlib=rb-4.1.0&q=85",
      title: "Charmantes Einfamilienhaus",
      location: "Kelsterbach",
      price: "450.000€",
      details: "180 m² • 5 Zimmer • Garten"
    },
    {
      image: "https://images.unsplash.com/photo-1580587771525-78b9dba3b914?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHwyfHxyZWFsJTIwZXN0YXRlJTIwaG9tZXxlbnwwfHx8fDE3NzM1Nzk2OTd8MA&ixlib=rb-4.1.0&q=85",
      title: "Moderne Luxusvilla",
      location: "Raunheim",
      price: "890.000€",
      details: "250 m² • 6 Zimmer • Pool"
    },
    {
      image: "https://images.unsplash.com/photo-1564013799919-ab600027ffc6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHwzfHxyZWFsJTIwZXN0YXRlJTIwaG9tZXxlbnwwfHx8fDE3NzM1Nzk2OTd8MA&ixlib=rb-4.1.0&q=85",
      title: "Exklusives Stadthaus",
      location: "Rüsselsheim",
      price: "620.000€",
      details: "200 m² • 5 Zimmer • Balkon"
    }
  ];

  const features = [
    "Über 15 Jahre Erfahrung",
    "Kostenlose Bewertung",
    "Professionelles Marketing",
    "Rechtssichere Verträge",
    "Persönliche Betreuung",
    "Regionales Netzwerk"
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-blue-50">
      {/* Header */}
      <header className="bg-white shadow-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Link to="/#portfolio" className="flex items-center gap-2 text-indigo-600 hover:text-indigo-700 transition-colors">
              <ArrowLeft size={20} />
              <span className="font-medium">Zurück zum Portfolio</span>
            </Link>
            <Button className="bg-indigo-500 hover:bg-indigo-600 text-white rounded-xl">
              Beratung anfragen
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-100 rounded-full text-indigo-700 text-sm font-medium">
                <Home size={16} />
                <span>Ihr Immobilienexperte</span>
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-800 leading-tight">
                Ihr Traumhaus wartet auf Sie
              </h1>
              <p className="text-lg text-slate-600 leading-relaxed">
                Als erfahrener Immobilienmakler in Kelsterbach und Umgebung helfen wir Ihnen, die perfekte Immobilie zu finden oder Ihre Immobilie erfolgreich zu verkaufen.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button size="lg" className="bg-indigo-500 hover:bg-indigo-600 text-white rounded-xl shadow-lg">
                  Kostenlose Bewertung
                </Button>
                <Button size="lg" variant="outline" className="border-2 border-indigo-300 text-slate-700 hover:bg-indigo-50 rounded-xl">
                  Aktuelle Angebote
                </Button>
              </div>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1605276374104-dee2a0ed3cd6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHw0fHxyZWFsJTIwZXN0YXRlJTIwaG9tZXxlbnwwfHx8fDE3NzM1Nzk2OTd8MA&ixlib=rb-4.1.0&q=85"
                alt="Beautiful Home"
                className="rounded-3xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-800 mb-4">
              Unsere Leistungen
            </h2>
            <div className="w-20 h-1 bg-indigo-500 mx-auto mb-6 rounded-full"></div>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Umfassender Service für Käufer, Verkäufer und Vermieter
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {services.map((service, index) => {
              const Icon = service.icon;
              return (
                <Card key={index} className="border-indigo-100 hover:shadow-xl transition-all rounded-2xl">
                  <CardContent className="pt-6">
                    <div className="flex items-start gap-4">
                      <div className="w-14 h-14 bg-indigo-100 rounded-2xl flex items-center justify-center flex-shrink-0">
                        <Icon className="text-indigo-600" size={28} />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-xl font-bold text-slate-800 mb-2">{service.title}</h3>
                        <p className="text-slate-600">{service.description}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Properties Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-indigo-50 to-blue-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-800 mb-4">
              Aktuelle Angebote
            </h2>
            <div className="w-20 h-1 bg-indigo-500 mx-auto rounded-full"></div>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {properties.map((property, index) => (
              <Card key={index} className="border-indigo-100 overflow-hidden hover:shadow-2xl transition-all rounded-2xl">
                <img
                  src={property.image}
                  alt={property.title}
                  className="w-full h-48 object-cover"
                />
                <CardContent className="pt-6">
                  <h3 className="text-xl font-bold text-slate-800 mb-2">{property.title}</h3>
                  <div className="flex items-center gap-2 text-slate-600 mb-3">
                    <MapPin size={16} className="text-indigo-600" />
                    <span>{property.location}</span>
                  </div>
                  <p className="text-sm text-slate-600 mb-4">{property.details}</p>
                  <div className="flex items-center justify-between">
                    <div className="text-2xl font-bold text-indigo-600">{property.price}</div>
                    <Button variant="outline" className="border-indigo-300 text-slate-700 hover:bg-indigo-50 rounded-xl">
                      Details
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-800 mb-6">
                Warum uns wählen?
              </h2>
              <div className="grid grid-cols-2 gap-4">
                {features.map((feature, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <div className="w-6 h-6 bg-indigo-500 rounded-full flex items-center justify-center flex-shrink-0">
                      <CheckCircle className="text-white" size={14} />
                    </div>
                    <span className="text-slate-700">{feature}</span>
                  </div>
                ))}
              </div>
              <p className="mt-6 text-slate-600 leading-relaxed">
                Mit über 15 Jahren Erfahrung im Immobilienmarkt kennen wir die Region wie unsere Westentasche. Vertrauen Sie auf unsere Expertise und lassen Sie uns gemeinsam Ihr Immobilienprojekt verwirklichen.
              </p>
            </div>
            <Card className="border-indigo-100 rounded-2xl shadow-xl">
              <CardContent className="pt-6 space-y-6">
                <h3 className="text-2xl font-bold text-slate-800">Kostenlose Erstberatung</h3>
                <p className="text-slate-600">
                  Vereinbaren Sie jetzt einen unverbindlichen Beratungstermin. Wir nehmen uns Zeit für Ihre Fragen und zeigen Ihnen, wie wir Sie unterstützen können.
                </p>
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <CheckCircle className="text-indigo-600" size={20} />
                    <span className="text-slate-700">Marktanalyse</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <CheckCircle className="text-indigo-600" size={20} />
                    <span className="text-slate-700">Preisempfehlung</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <CheckCircle className="text-indigo-600" size={20} />
                    <span className="text-slate-700">Verkaufsstrategie</span>
                  </div>
                </div>
                <Button className="w-full bg-indigo-500 hover:bg-indigo-600 text-white rounded-xl">
                  Jetzt Termin vereinbaren
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-indigo-50 to-blue-50">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-800 mb-6">
            Kontaktieren Sie uns
          </h2>
          <p className="text-lg text-slate-600 mb-8">
            Wir sind für Sie da - persönlich, kompetent und mit Leidenschaft für Immobilien
          </p>
          <div className="grid md:grid-cols-3 gap-6 mb-8">
            <Card className="border-indigo-100 rounded-2xl">
              <CardContent className="pt-6 text-center">
                <Phone className="text-indigo-600 mx-auto mb-3" size={32} />
                <div className="font-semibold text-slate-800 mb-1">Telefon</div>
                <div className="text-slate-600">+49 (0) 6107 567890</div>
              </CardContent>
            </Card>
            <Card className="border-indigo-100 rounded-2xl">
              <CardContent className="pt-6 text-center">
                <Mail className="text-indigo-600 mx-auto mb-3" size={32} />
                <div className="font-semibold text-slate-800 mb-1">E-Mail</div>
                <div className="text-slate-600">info@immobilien-experte.de</div>
              </CardContent>
            </Card>
            <Card className="border-indigo-100 rounded-2xl">
              <CardContent className="pt-6 text-center">
                <MapPin className="text-indigo-600 mx-auto mb-3" size={32} />
                <div className="font-semibold text-slate-800 mb-1">Büro</div>
                <div className="text-slate-600">Bahnhofstraße 23, Kelsterbach</div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-800 text-white py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-slate-300">
            © 2025 Immobilien Experte • Erstellt von{' '}
            <Link to="/#portfolio" className="text-indigo-400 hover:text-indigo-300">
              WebWerk Kelsterbach
            </Link>
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Immobilienmakler;